import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import styled from 'styled-components'
import {
  Box,
  Button,
  ButtonMenu,
  ButtonMenuItem,
  Flex,
  Text,
  ArrowDownIcon,
  Toggle,
  useMatchBreakpoints,
} from '@pancakeswap/uikit'
import Page from 'components/Layout/Page'
import { AppBody } from 'components/App'
import { AutoColumn } from 'components/Layout/Column'
import { AutoRow, RowBetween, RowFixed } from 'components/Layout/Row'
import ConnectWalletButton from 'components/ConnectWalletButton'
import QuestionHelper from 'components/QuestionHelper'
import TradingView from 'components/TradingView'
import XrplCurrencyInputPanel from 'components/CurrencyInputPanel/XrplCurrencyInputPanel'
import { Input as NumericalInput } from 'components/CurrencyInputPanel/NumericalInput'
import { useUserLimitOrdersEnabled, useUserSlippageTolerance } from 'state/user/hooks'
import { LightGreyCard } from 'components/Card'
import {
  addCustomTokens,
  createTrustline,
  fetchBithompTokenInfo,
  fetchBalance,
  fetchBithompAmmByPair,
  fetchOrderBook,
  fetchOpenOrders,
  getTokenByKey,
  getTokenKey,
  getXrplTxUrl,
  hasTrustline,
  cancelLimitOrder,
  quoteBookSwap,
  quotePathSwap,
  quoteAmmSwap,
  createLimitOrder,
  XrplOrderBook,
  XrplOpenOrder,
  swapOrderBook,
  swapExactIn,
  useXrpl,
  useXrplTokens,
  XrplCurrency,
} from '../../xrpl'
import { fromXrplAmount } from '../../xrpl/amounts'
import CurrencyInputHeader from './components/CurrencyInputHeader'
import TradePrice from './components/TradePrice'
import { ArrowWrapper, ErrorText, Wrapper } from './components/styleds'

const TradePage = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
`

const TradeLayout = styled.div`
  width: 100%;
  max-width: 1400px;
  display: grid;
  grid-template-columns: 1fr;
  gap: 16px;
  padding: 0 0 24px;
  align-items: start;

  ${({ theme }) => theme.mediaQueries.sm} {
    padding: 0 0 24px;
  }

  ${({ theme }) => theme.mediaQueries.md} {
    padding: 0 0 24px;
  }

  ${({ theme }) => theme.mediaQueries.lg} {
    max-width: 1400px;
    grid-template-columns: minmax(420px, 440px) minmax(0, 1fr);
    gap: 24px;
    padding: 0 0 24px;
  }
`

const TxRow = styled.div`
  margin-top: 16px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
`

const TxLink = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 6px 12px;
  border-radius: 999px;
  background: ${({ theme }) => theme.colors.input};
  border: 1px solid rgba(255, 255, 255, 0.08);
  color: ${({ theme }) => theme.colors.primary};
  font-size: 12px;
  font-weight: 600;
  text-decoration: none;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;

  &:hover {
    border-color: rgba(245, 200, 66, 0.6);
    box-shadow: 0 0 12px rgba(245, 200, 66, 0.2);
  }
`

const TxLinkHint = styled.span`
  color: ${({ theme }) => theme.colors.textSubtle};
  font-weight: 400;
  font-size: 11px;
`

const formatTxHash = (hash: string) => {
  if (!hash) return ''
  if (hash.length <= 14) return hash
  return `${hash.slice(0, 6)}...${hash.slice(-6)}`
}

const TradeHeader = styled.div`
  grid-column: 1 / -1;
`

const LeftPanel = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`

const RightPanel = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`

const SwapCard = styled(AppBody)`
  background: #0b0b0b;
  border: 1px solid #f5c842;
  box-shadow: 0 0 25px rgba(245, 200, 66, 0.15);
  border-radius: 20px;
  max-width: 100%;
  margin: 0;
`

const SwapCtaButton = styled(Button)`
  height: 52px;
  font-weight: 600;
  font-size: 16px;
  background: linear-gradient(180deg, #f5c842 0%, #d9a900 100%);
  color: #1a1a1a;
  box-shadow: 0 6px 18px rgba(245, 200, 66, 0.22);

  &:hover:not(:disabled) {
    box-shadow: 0 8px 22px rgba(245, 200, 66, 0.32);
    filter: brightness(1.02);
  }

  &:active:not(:disabled) {
    transform: translateY(1px);
    filter: brightness(0.98);
  }

  &:disabled {
    opacity: 0.6;
  }
`

const SwapConnectButton = styled(ConnectWalletButton)`
  height: 52px;
  font-weight: 600;
  font-size: 16px;
  background: linear-gradient(180deg, #f5c842 0%, #d9a900 100%);
  color: #1a1a1a;
  box-shadow: 0 6px 18px rgba(245, 200, 66, 0.22);

  &:hover:not(:disabled) {
    box-shadow: 0 8px 22px rgba(245, 200, 66, 0.32);
    filter: brightness(1.02);
  }

  &:active:not(:disabled) {
    transform: translateY(1px);
    filter: brightness(0.98);
  }

  &:disabled {
    opacity: 0.6;
  }
`

const PriceHeaderCard = styled(LightGreyCard)`
  padding: 4px 8px;
  border-radius: 10px;
  background: #0b0b0b;
  border: none;
  box-shadow: none;
`

const PriceHeaderMain = styled(Flex)`
  align-items: center;
  gap: 16px;
`

const PriceHeaderStats = styled(Flex)`
  align-items: center;
  gap: 16px;
`

const TV_PAIR_OVERRIDES: Record<string, string> = (() => {
  const raw = process.env.REACT_APP_TV_PAIR_OVERRIDES
  if (!raw) return {}
  try {
    const parsed = JSON.parse(raw) as Record<string, string>
    return Object.entries(parsed).reduce<Record<string, string>>((acc, [key, value]) => {
      if (!key || !value) return acc
      acc[key.toUpperCase()] = value
      return acc
    }, {})
  } catch (error) {
    console.warn('Invalid REACT_APP_TV_PAIR_OVERRIDES', error)
    return {}
  }
})()

const STABLE_ALIASES = new Set(['USD', 'USDT', 'USDC', 'USDS', 'USDP', 'RLUSD', 'DAI', 'TUSD'])

const DetailsCard = styled(LightGreyCard)`
  padding: 16px;
  border-radius: 20px;
  border: 1px solid rgba(255, 255, 255, 0.08);
`

const LimitInputContainer = styled.div`
  border-radius: 16px;
  background: ${({ theme }) => theme.colors.input};
  box-shadow: ${({ theme }) => theme.shadows.inset};
  padding: 8px 16px;
`

const LimitInputRow = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
`

const OrdersCard = styled(LightGreyCard)`
  padding: 16px;
  border-radius: 20px;
  border: 1px solid rgba(255, 255, 255, 0.08);
`

const MarketCard = styled(LightGreyCard)<{ $active?: boolean }>`
  padding: 8px;
  border-radius: 20px;
  border: 1px solid ${({ $active }) => ($active ? '#f5c842' : 'rgba(255, 255, 255, 0.08)')};
  box-shadow: ${({ $active }) => ($active ? '0 0 18px rgba(245, 200, 66, 0.18)' : 'none')};
`

const OrderRow = styled.div`
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 16px;
  padding: 16px;
  background: ${({ theme }) => theme.colors.backgroundAlt};
`

const MarketRow = styled.div`
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 16px;
  padding: 8px;
  background: ${({ theme }) => theme.colors.backgroundAlt};
`

const MarketHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
`

const MarketMeta = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`

const MarketPriceLine = styled.div`
  display: flex;
  align-items: baseline;
  gap: 8px;
  flex-wrap: wrap;
`

const MarketPrice = styled(Text)`
  font-size: 18px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.text};
`

const MarketPriceSub = styled(Text)`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.textSubtle};
`

const OrderbookTable = styled.div`
  max-width: 520px;
  margin: 0 auto;
`

const OrderbookMidRow = styled.div`
  margin: 8px 0 12px;
  padding: 6px 8px;
  border-top: 1px solid rgba(255, 255, 255, 0.06);
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
`

const OrderbookGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  row-gap: 16px;

  ${({ theme }) => theme.mediaQueries.md} {
    grid-template-columns: 1fr auto 1fr;
    column-gap: 24px;
    row-gap: 0;
    align-items: start;
  }
`

const OrderbookSide = styled.div<{ $side: 'ask' | 'bid' }>`
  display: flex;
  flex-direction: column;
  align-items: flex-start;

  ${({ theme, $side }) => theme.mediaQueries.md} {
    align-items: ${({ $side }) => ($side === 'ask' ? 'flex-end' : 'flex-start')};
  }
`

const OrderbookDivider = styled.div`
  display: none;
  width: 1px;
  height: 100%;
  background: rgba(255, 255, 255, 0.05);
  justify-self: center;
  align-self: stretch;

  ${({ theme }) => theme.mediaQueries.md} {
    display: block;
  }
`

const OrderbookHeader = styled.div`
  display: grid;
  grid-template-columns: 80px 100px 60px;
  gap: 8px;
  width: 256px;
  padding-bottom: 8px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
`

const OrderbookRow = styled.div<{ $side: 'bid' | 'ask'; $depth?: number }>`
  display: grid;
  grid-template-columns: 80px 100px 60px;
  gap: 8px;
  width: 256px;
  padding: 8px 0;
  color: ${({ $side }) => ($side === 'bid' ? '#2ECC71' : '#FF5C5C')};
  font-size: 11px;
  line-height: 1;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    bottom: 0;
    ${({ $side }) => ($side === 'bid' ? 'left: 0;' : 'right: 0;')}
    width: ${({ $depth }) => `${Math.min(Math.max($depth || 0, 0), 1) * 100}%`};
    background: ${({ $side }) => ($side === 'bid' ? 'rgba(46, 204, 113, 0.06)' : 'rgba(255, 92, 92, 0.06)')};
    z-index: 0;
  }

  > * {
    position: relative;
    z-index: 1;
  }

  &:hover {
    background: rgba(255, 255, 255, 0.03);
  }
`

const OrderbookCell = styled(Text)<{
  $align?: 'left' | 'right'
  $muted?: boolean
  $side?: 'bid' | 'ask'
  $flow?: 'in' | 'out'
}>`
  text-align: ${({ $align, $side, $flow }) => {
    if ($flow === 'in') {
      if ($side === 'ask') return 'right'
      if ($side === 'bid') return 'left'
    }
    return $align || 'right'
  }};
  font-size: 11px;
  color: ${({ theme, $muted, $side }) => {
    if ($muted) return theme.colors.textSubtle
    if ($side === 'bid') return '#2ECC71'
    if ($side === 'ask') return '#FF5C5C'
    return theme.colors.text
  }};
`

const OrderbookEmptyText = styled(Text)`
  color: ${({ theme }) => theme.colors.textDisabled};
  font-size: 11px;
`

const MarketTabs = styled.div`
  display: flex;
  align-items: flex-end;
  gap: 24px;
`

const MarketTab = styled.button<{ $active?: boolean }>`
  border: none;
  background: transparent;
  padding: 0 0 8px;
  color: ${({ theme, $active }) => ($active ? theme.colors.text : theme.colors.textSubtle)};
  font-size: 13px;
  font-weight: ${({ $active }) => ($active ? 600 : 500)};
  cursor: pointer;
  border-bottom: ${({ theme, $active }) => ($active ? `3px solid ${theme.colors.primary}` : '3px solid transparent')};
  opacity: ${({ $active }) => ($active ? 1 : 0.6)};
  box-shadow: none;
  outline: none;
`

const ChartContainer = styled.div`
  height: 360px;
  border-radius: 16px;
  overflow: hidden;
  background: ${({ theme }) => theme.colors.backgroundAlt};
`

const formatAmount = (value: number | string, precision = 6) => {
  const numeric = Number(value)
  if (!Number.isFinite(numeric) || numeric <= 0) {
    return '0'
  }
  return numeric.toLocaleString(undefined, {
    maximumFractionDigits: precision,
  })
}

const formatDisplayAmount = (value: string, precision = 6) => {
  const numeric = Number(value)
  if (!Number.isFinite(numeric)) {
    return value
  }
  if (numeric === 0) {
    return '0'
  }
  const fixed = numeric.toFixed(precision)
  return fixed.replace(/\.?0+$/, '')
}

const formatSecondaryValue = (amount: string, token?: XrplCurrency | null) => {
  const numeric = Number(amount)
  if (!token || !Number.isFinite(numeric) || numeric <= 0) {
    return null
  }
  if (token.priceUsd && Number.isFinite(token.priceUsd)) {
    return `~$${formatAmount(numeric * token.priceUsd, 2)}`
  }
  if (token.priceXrp && Number.isFinite(token.priceXrp)) {
    return `~${formatAmount(numeric * token.priceXrp, 4)} XRP`
  }
  return null
}

const rippleEpochToDate = (expiration: number) => new Date((expiration + 946684800) * 1000)

const isHexCurrencyCode = (value: string) => /^[A-F0-9]{40}$/i.test(value)

const decodeHexCurrency = (value: string) => {
  if (!isHexCurrencyCode(value)) return null
  const chars = []
  for (let i = 0; i < value.length; i += 2) {
    const code = parseInt(value.slice(i, i + 2), 16)
    if (code) {
      chars.push(String.fromCharCode(code))
    }
  }
  const decoded = chars.join('').trim()
  return decoded || null
}

const shortIssuer = (issuer?: string) => {
  if (!issuer) return ''
  return `${issuer.slice(0, 4)}...${issuer.slice(-4)}`
}

const matchAsset = (token: XrplCurrency, asset: XrplCurrency) => {
  if (token.isNative || token.currency === 'XRP') {
    return asset.currency === 'XRP'
  }
  return token.currency === asset.currency && token.issuer === asset.issuer
}

const getPriceImpactSeverity = (impactPct?: number) => {
  if (impactPct === undefined || Number.isNaN(impactPct)) return 0
  if (impactPct >= 15) return 4
  if (impactPct >= 5) return 3
  if (impactPct >= 3) return 2
  if (impactPct >= 1) return 1
  return 0
}

const Swap: React.FC = () => {
  const { isMobile } = useMatchBreakpoints()
  const { account, signAndSubmit } = useXrpl()
  const { tokens, error: tokensError } = useXrplTokens()
  const [limitOrdersEnabled] = useUserLimitOrdersEnabled()
  const [userSlippageTolerance] = useUserSlippageTolerance()
  const [fromKey, setFromKey] = useState('')
  const [toKey, setToKey] = useState('')
  const [amountIn, setAmountIn] = useState('')
  const [amountOut, setAmountOut] = useState('0')
  const [balanceIn, setBalanceIn] = useState('0')
  const [balanceOut, setBalanceOut] = useState('0')
  const [poolInfo, setPoolInfo] = useState(null)
  const [trustlineMissing, setTrustlineMissing] = useState(false)
  const [txHash, setTxHash] = useState('')
  const [error, setError] = useState('')
  const [showInverted, setShowInverted] = useState(false)
  const [limitOrderMode, setLimitOrderMode] = useState(false)
  const [limitPrice, setLimitPrice] = useState('')
  const [limitExpiryEnabled, setLimitExpiryEnabled] = useState(false)
  const [limitExpiryMinutes, setLimitExpiryMinutes] = useState('60')
  const [openOrders, setOpenOrders] = useState<XrplOpenOrder[]>([])
  const [openOrdersLoading, setOpenOrdersLoading] = useState(false)
  const [openOrdersError, setOpenOrdersError] = useState('')
  const [ordersRefreshId, setOrdersRefreshId] = useState(0)
  const [isMarketDisplayed, setIsMarketDisplayed] = useState(!isMobile)
  const [marketView, setMarketView] = useState<'orderbook' | 'chart'>('orderbook')
  const [orderBook, setOrderBook] = useState<XrplOrderBook | null>(null)
  const [orderBookLoading, setOrderBookLoading] = useState(false)
  const [orderBookError, setOrderBookError] = useState('')
  const [orderBookRefreshId, setOrderBookRefreshId] = useState(0)
  const priceRequests = useRef<Set<string>>(new Set())
  const [tokenInfoRefreshId, setTokenInfoRefreshId] = useState(0)
  const tokenInfoRetryRef = useRef<number | null>(null)
  const [bookQuote, setBookQuote] = useState<{ amountOut: string } | null>(null)
  const [pathQuote, setPathQuote] = useState<{ amountOut: string; paths?: any[] } | null>(null)
  const bookQuoteId = useRef(0)
  const pathQuoteId = useRef(0)
  const poolRetryRef = useRef<number | null>(null)
  const [poolRetryId, setPoolRetryId] = useState(0)

  const fromToken = useMemo(() => getTokenByKey(fromKey, tokens), [fromKey, tokens])
  const toToken = useMemo(() => getTokenByKey(toKey, tokens), [toKey, tokens])

  useEffect(() => {
    if (!tokens.length) return
    const defaultFrom = getTokenKey(tokens[0])
    const defaultTo = getTokenKey(tokens[1] ?? tokens[0])
    if (!getTokenByKey(fromKey, tokens)) {
      setFromKey(defaultFrom)
    }
    if (!getTokenByKey(toKey, tokens)) {
      setToKey(defaultTo)
    }
  }, [fromKey, toKey, tokens])

  useEffect(() => {
    if (!limitOrdersEnabled && limitOrderMode) {
      setLimitOrderMode(false)
      setLimitPrice('')
    }
  }, [limitOrdersEnabled, limitOrderMode])

  useEffect(() => {
    let isMounted = true

    const loadOrders = async () => {
      if (!limitOrdersEnabled || !account) {
        if (isMounted) {
          setOpenOrders([])
          setOpenOrdersError('')
          setOpenOrdersLoading(false)
        }
        return
      }

      setOpenOrdersLoading(true)
      try {
        const orders = await fetchOpenOrders({ account })
        if (isMounted) {
          setOpenOrders(orders)
          setOpenOrdersError('')
        }
      } catch (fetchError) {
        if (isMounted) {
          setOpenOrders([])
          setOpenOrdersError(fetchError.message || 'Failed to load open orders')
        }
      } finally {
        if (isMounted) {
          setOpenOrdersLoading(false)
        }
      }
    }

    loadOrders()

    return () => {
      isMounted = false
    }
  }, [account, limitOrdersEnabled, ordersRefreshId])

  useEffect(() => {
    let isMounted = true

    const loadOrderBook = async () => {
      if (!isMarketDisplayed || marketView !== 'orderbook' || !fromToken || !toToken || fromKey === toKey) {
        if (isMounted) {
          setOrderBook(null)
          setOrderBookError('')
          setOrderBookLoading(false)
        }
        return
      }

      setOrderBookLoading(true)
      try {
        const book = await fetchOrderBook({ base: fromToken, quote: toToken })
        if (isMounted) {
          setOrderBook(book)
          setOrderBookError('')
        }
      } catch (bookError) {
        if (isMounted) {
          setOrderBook(null)
          setOrderBookError(bookError.message || 'Failed to load order book')
        }
      } finally {
        if (isMounted) {
          setOrderBookLoading(false)
        }
      }
    }

    loadOrderBook()

    return () => {
      isMounted = false
    }
  }, [account, fromKey, fromToken, isMarketDisplayed, marketView, orderBookRefreshId, toKey, toToken])

  useEffect(() => {
    let interval: ReturnType<typeof setInterval> | null = null
    if (isMarketDisplayed && marketView === 'orderbook' && fromToken && toToken && fromKey !== toKey) {
      interval = setInterval(() => {
        setOrderBookRefreshId((current) => current + 1)
      }, 15000)
    }

    return () => {
      if (interval) {
        clearInterval(interval)
      }
    }
  }, [fromKey, fromToken, isMarketDisplayed, marketView, toKey, toToken])

  const slippagePct = userSlippageTolerance / 100
  const getTokenPriceXrp = useCallback((token?: XrplCurrency | null) => {
    if (!token) return undefined
    if (token.isNative || token.currency === 'XRP') return 1
    const price = Number(token.priceXrp)
    if (Number.isFinite(price) && price > 0) return price
    return undefined
  }, [])
  const getTokenPriceXrp24h = useCallback((token?: XrplCurrency | null) => {
    if (!token) return undefined
    if (token.isNative || token.currency === 'XRP') return 1
    const price = Number(token.priceXrp24h)
    if (Number.isFinite(price) && price > 0) return price
    return undefined
  }, [])
  const resolveToken = useCallback(
    (token?: XrplCurrency | null) => {
      if (!token) return null
      const resolved = getTokenByKey(getTokenKey(token), tokens)
      return resolved || token
    },
    [tokens],
  )

  const orderBookDepth = useMemo(() => {
    if (!orderBook) {
      return { asks: [], bids: [], maxAsk: 0, maxBid: 0 }
    }
    const buildDepth = (rows: XrplOrderBookLevel[]) => {
      let cumulative = 0
      const levels = rows.map((row) => {
        cumulative += row.total
        return { ...row, depth: cumulative }
      })
      return { levels, maxDepth: cumulative }
    }
    const asksSlice = (orderBook.asks || []).slice(0, 10)
    const bidsSlice = (orderBook.bids || []).slice(0, 10)
    const askDepth = buildDepth(asksSlice)
    const bidDepth = buildDepth(bidsSlice)
    return { asks: askDepth.levels, bids: bidDepth.levels, maxAsk: askDepth.maxDepth, maxBid: bidDepth.maxDepth }
  }, [orderBook])
  const resolveTokenLabel = useCallback(
    (currencyCode: string, issuer?: string) => {
      if (currencyCode === 'XRP') {
        return 'XRP'
      }
      const match = tokens.find(
        (token) => !token.isNative && token.currency === currencyCode && token.issuer === issuer,
      )
      if (match?.symbol) {
        return match.symbol
      }
      return decodeHexCurrency(currencyCode) || currencyCode
    },
    [tokens],
  )

  const mapOfferAmount = useCallback(
    (amount: any) => {
      if (typeof amount === 'string') {
        return {
          currency: 'XRP',
          issuer: undefined,
          value: Number(fromXrplAmount(amount)),
          symbol: 'XRP',
          isNative: true,
        }
      }
      const currencyCode = amount?.currency || 'XRP'
      const issuer = amount?.issuer
      const value = Number(fromXrplAmount(amount))
      const symbol = resolveTokenLabel(currencyCode, issuer)
      return {
        currency: currencyCode,
        issuer,
        value,
        symbol,
        isNative: currencyCode === 'XRP',
      }
    },
    [resolveTokenLabel],
  )

  const formattedOrders = useMemo(() => {
    return openOrders.map((order) => {
      const sellAmount = mapOfferAmount(order.takerGets)
      const buyAmount = mapOfferAmount(order.takerPays)
      const price = sellAmount.value > 0 ? buyAmount.value / sellAmount.value : 0
      return {
        order,
        sellAmount,
        buyAmount,
        price,
        expiration:
          order.expiration && Number.isFinite(order.expiration)
            ? rippleEpochToDate(order.expiration).toLocaleString()
            : 'None',
      }
    })
  }, [mapOfferAmount, openOrders])

  const chartSymbol = useMemo(() => {
    const fromSymbol = fromToken?.symbol?.toUpperCase()
    const toSymbol = toToken?.symbol?.toUpperCase()
    if (!fromSymbol || !toSymbol) return null

    const pairKey = `${fromSymbol}/${toSymbol}`
    const reverseKey = `${toSymbol}/${fromSymbol}`
    if (TV_PAIR_OVERRIDES[pairKey]) return TV_PAIR_OVERRIDES[pairKey]
    if (TV_PAIR_OVERRIDES[reverseKey]) return TV_PAIR_OVERRIDES[reverseKey]

    const normalizeStable = (symbol: string) => (STABLE_ALIASES.has(symbol) ? 'USD' : symbol)
    const base = normalizeStable(fromSymbol)
    const quote = normalizeStable(toSymbol)
    if (base === quote) return null

    const mapXrpPairs: Record<string, string> = {
      'XRP/USD': 'BITSTAMP:XRPUSD',
      'XRP/USDT': 'BINANCE:XRPUSDT',
      'XRP/USDC': 'COINBASE:XRPUSD',
      'XRP/BTC': 'BINANCE:XRPBTC',
      'XRP/ETH': 'BINANCE:XRPETH',
    }

    if (base === 'XRP' && mapXrpPairs[`XRP/${quote}`]) {
      return mapXrpPairs[`XRP/${quote}`]
    }
    if (quote === 'XRP' && mapXrpPairs[`XRP/${base}`]) {
      return mapXrpPairs[`XRP/${base}`]
    }
    if (base === 'XRP' || quote === 'XRP') {
      return mapXrpPairs['XRP/USD']
    }

    const forexPairs: Record<string, string> = {
      'USD/JPY': 'FX:USDJPY',
      'USD/EUR': 'FX:USDEUR',
      'USD/GBP': 'FX:USDGBP',
    }
    if (forexPairs[pairKey]) return forexPairs[pairKey]
    if (forexPairs[reverseKey]) return forexPairs[reverseKey]

    return null
  }, [fromToken?.symbol, toToken?.symbol])

  const ammQuoteOut = useMemo(() => {
    if (!poolInfo || !fromToken || !toToken || fromKey === toKey) {
      return '0'
    }
    const numericAmountIn = Number(amountIn)
    if (!numericAmountIn) {
      return '0'
    }
    return quoteAmmSwap({
      amm: poolInfo,
      amountIn,
      from: fromToken,
      to: toToken,
    })
  }, [amountIn, fromKey, fromToken, poolInfo, toKey, toToken])

  const swapStats = useMemo(() => {
    if (limitOrderMode) {
      return null
    }
    if (!poolInfo || !fromToken || !toToken) {
      return null
    }
    const numericAmountIn = Number(amountIn)
    const numericAmountOut = Number(ammQuoteOut)
    if (!numericAmountIn || !numericAmountOut) {
      return null
    }
    const fromIsA = matchAsset(fromToken, poolInfo.assetA)
    const reserveIn = Number(fromIsA ? poolInfo.reserveA : poolInfo.reserveB)
    const reserveOut = Number(fromIsA ? poolInfo.reserveB : poolInfo.reserveA)
    const spotPrice = reserveIn > 0 ? reserveOut / reserveIn : 0
    const executionPrice = numericAmountOut / numericAmountIn
    const priceImpact = spotPrice > 0 ? Math.max((spotPrice - executionPrice) / spotPrice, 0) : 0
    const feeRate = poolInfo.tradingFee / 100000
    const lpFee = numericAmountIn * feeRate
    const minReceived = numericAmountOut * (1 - userSlippageTolerance / 10000)

    return {
      executionPrice,
      spotPrice,
      priceImpactPct: priceImpact * 100,
      lpFee,
      minReceived,
      feeRate,
    }
  }, [limitOrderMode, poolInfo, fromToken, toToken, amountIn, ammQuoteOut, userSlippageTolerance])
  const priceForDisplay = useMemo(() => {
    if (!fromToken || !toToken) {
      return undefined
    }

    let executionPrice = swapStats?.executionPrice
    if ((!executionPrice || executionPrice <= 0) && Number(amountIn) > 0 && Number(amountOut) > 0) {
      executionPrice = Number(amountOut) / Number(amountIn)
    }
    if (limitOrderMode) {
      const limitValue = Number(limitPrice || '0')
      if (Number.isFinite(limitValue) && limitValue > 0) {
        executionPrice = limitValue
      }
    }
    if (!executionPrice || executionPrice <= 0) {
      if (poolInfo) {
        const fromIsA = matchAsset(fromToken, poolInfo.assetA)
        const reserveIn = Number(fromIsA ? poolInfo.reserveA : poolInfo.reserveB)
        const reserveOut = Number(fromIsA ? poolInfo.reserveB : poolInfo.reserveA)
        if (reserveIn > 0 && reserveOut > 0) {
          executionPrice = reserveOut / reserveIn
        }
      }
    }

    if (!executionPrice || executionPrice <= 0) {
      const fromPriceXrp = getTokenPriceXrp(fromToken)
      const toPriceXrp = getTokenPriceXrp(toToken)
      if (fromPriceXrp && toPriceXrp) {
        executionPrice = fromPriceXrp / toPriceXrp
      }
    }

    if (!executionPrice || executionPrice <= 0) {
      return undefined
    }

    const base = { symbol: fromToken.symbol }
    const quote = { symbol: toToken.symbol }
    const invertedValue = executionPrice === 0 ? 0 : 1 / executionPrice
    return {
      baseCurrency: base,
      quoteCurrency: quote,
      toSignificant: (digits = 6) => formatAmount(executionPrice, digits),
      invert: () => ({
        baseCurrency: quote,
        quoteCurrency: base,
        toSignificant: (digits = 6) => formatAmount(invertedValue, digits),
      }),
    } as any
  }, [
    amountIn,
    amountOut,
    fromToken,
    getTokenPriceXrp,
    limitOrderMode,
    limitPrice,
    poolInfo,
    swapStats?.executionPrice,
    toToken,
  ])

  const autoRouteMaxImpactPct = useMemo(() => Math.max(slippagePct, 1), [slippagePct])
  const autoRoute = useMemo(() => {
    if (limitOrderMode) {
      return 'limit' as const
    }
    if (!fromToken || !toToken || fromKey === toKey) {
      return 'none' as const
    }
    const ammOut = Number(ammQuoteOut || '0')
    const bookOut = Number(bookQuote?.amountOut || '0')
    const pathOut = Number(pathQuote?.amountOut || '0')
    const ammImpact = swapStats?.priceImpactPct ?? 0

    if (poolInfo && ammOut > 0) {
      if (bookOut > 0) {
        if (ammImpact > autoRouteMaxImpactPct) {
          return 'orderbook' as const
        }
        return bookOut > ammOut ? ('orderbook' as const) : ('amm' as const)
      }
      return 'amm' as const
    }

    if (bookOut > 0) {
      return 'orderbook' as const
    }
    if (pathOut > 0) {
      return 'path' as const
    }
    return 'none' as const
  }, [
    ammQuoteOut,
    autoRouteMaxImpactPct,
    bookQuote,
    fromKey,
    fromToken,
    limitOrderMode,
    pathQuote,
    poolInfo,
    swapStats?.priceImpactPct,
    toKey,
    toToken,
  ])
  const orderBookSummary = useMemo(() => {
    if (!orderBook?.asks?.length || !orderBook?.bids?.length) {
      return null
    }
    const bestAsk = Number(orderBook.asks[0].price)
    const bestBid = Number(orderBook.bids[0].price)
    if (!Number.isFinite(bestAsk) || !Number.isFinite(bestBid) || bestAsk <= 0 || bestBid <= 0) {
      return null
    }
    const mid = (bestAsk + bestBid) / 2
    const spread = bestAsk - bestBid
    const spreadPct = mid > 0 ? (spread / mid) * 100 : null
    return { mid, spread, spreadPct }
  }, [orderBook])
  const orderBookMidDisplay = useMemo(() => {
    if (orderBookSummary?.mid) {
      return {
        value: formatAmount(orderBookSummary.mid, 6),
        symbol: toToken?.symbol || '',
      }
    }
    if (!priceForDisplay?.baseCurrency || !priceForDisplay?.quoteCurrency) {
      return null
    }
    const fallback = priceForDisplay.toSignificant(6)
    if (!fallback) {
      return null
    }
    return {
      value: fallback,
      symbol: priceForDisplay.quoteCurrency.symbol || '',
    }
  }, [orderBookSummary, priceForDisplay, toToken?.symbol])
  const marketPrice = useMemo(() => {
    if (!priceForDisplay?.baseCurrency || !priceForDisplay?.quoteCurrency) {
      return null
    }
    const inverted = !showInverted
    const value = inverted
      ? priceForDisplay.invert()?.toSignificant(6)
      : priceForDisplay.toSignificant(6)
    if (!value) {
      return null
    }
    const symbol = inverted ? priceForDisplay.baseCurrency.symbol : priceForDisplay.quoteCurrency.symbol
    const numeric = Number(value)
    if (!Number.isFinite(numeric) || numeric <= 0) {
      return { value, symbol, usd: null as number | null }
    }
    const quoteToken = resolveToken(inverted ? fromToken : toToken)
    const usdPrice = quoteToken?.priceUsd
    const usd = Number.isFinite(usdPrice) && usdPrice ? numeric * usdPrice : null
    return { value, symbol, usd }
  }, [fromToken, priceForDisplay, resolveToken, showInverted, toToken])
  const headerPriceText = useMemo(() => {
    if (!priceForDisplay?.baseCurrency || !priceForDisplay?.quoteCurrency) {
      return '-'
    }
    const formatted = showInverted
      ? priceForDisplay.toSignificant(6)
      : priceForDisplay.invert()?.toSignificant(6)
    if (!formatted) {
      return '-'
    }
    const label = showInverted
      ? `${priceForDisplay.quoteCurrency.symbol} per ${priceForDisplay.baseCurrency.symbol}`
      : `${priceForDisplay.baseCurrency.symbol} per ${priceForDisplay.quoteCurrency.symbol}`
    return `${formatted} ${label}`
  }, [priceForDisplay, showInverted])
  const { priceChangePct, volumeXrp, liquidityXrp } = useMemo(() => {
    if (!fromToken || !toToken) {
      return { priceChangePct: undefined, volumeXrp: undefined, liquidityXrp: undefined }
    }

    const resolvedFrom = resolveToken(fromToken)
    const resolvedTo = resolveToken(toToken)
    const fromPrice = getTokenPriceXrp(resolvedFrom)
    const toPrice = getTokenPriceXrp(resolvedTo)

    let pairPrice: number | undefined
    if (poolInfo) {
      const fromIsA = matchAsset(fromToken, poolInfo.assetA)
      const reserveIn = Number(fromIsA ? poolInfo.reserveA : poolInfo.reserveB)
      const reserveOut = Number(fromIsA ? poolInfo.reserveB : poolInfo.reserveA)
      if (reserveIn > 0 && reserveOut > 0) {
        pairPrice = reserveOut / reserveIn
      }
    } else if (fromPrice && toPrice) {
      pairPrice = toPrice / fromPrice
    }

    const fromPrice24h = getTokenPriceXrp24h(resolvedFrom)
    const toPrice24h = getTokenPriceXrp24h(resolvedTo)
    let changePct: number | undefined
    if (pairPrice && fromPrice24h && toPrice24h) {
      const pairPrice24h = toPrice24h / fromPrice24h
      if (pairPrice24h > 0) {
        changePct = ((pairPrice - pairPrice24h) / pairPrice24h) * 100
      }
    }

    const volumeToken = resolvedTo?.volume24h ? resolvedTo : resolvedFrom?.volume24h ? resolvedFrom : null
    let volumeValue: number | undefined
    if (volumeToken?.volume24h) {
      const volumePrice = getTokenPriceXrp(volumeToken)
      if (volumePrice) {
        volumeValue = volumeToken.volume24h * volumePrice
      }
    }

    let liquidityValue: number | undefined
    if (poolInfo) {
      const assetA = resolveToken(poolInfo.assetA)
      const assetB = resolveToken(poolInfo.assetB)
      const priceA = getTokenPriceXrp(assetA)
      const priceB = getTokenPriceXrp(assetB)
      const reserveA = Number(poolInfo.reserveA)
      const reserveB = Number(poolInfo.reserveB)
      if (priceA && priceB && reserveA && reserveB) {
        liquidityValue = reserveA * priceA + reserveB * priceB
      }
    }

    return { priceChangePct: changePct, volumeXrp: volumeValue, liquidityXrp: liquidityValue }
  }, [fromToken, getTokenPriceXrp, getTokenPriceXrp24h, poolInfo, resolveToken, toToken])
  const priceChangeText =
    priceChangePct !== undefined
      ? `${priceChangePct >= 0 ? '+' : ''}${priceChangePct.toFixed(2)}%`
      : '-'
  const priceChangeColor = priceChangePct === undefined ? 'textSubtle' : priceChangePct >= 0 ? 'success' : 'failure'
  const volumeText = volumeXrp !== undefined ? `${formatAmount(volumeXrp, 2)} XRP` : '-'
  const liquidityText = liquidityXrp !== undefined ? `${formatAmount(liquidityXrp, 2)} XRP` : '-'

  const fromSecondaryValue = useMemo(
    () => formatSecondaryValue(amountIn, fromToken),
    [amountIn, fromToken],
  )
  const toSecondaryValue = useMemo(
    () => formatSecondaryValue(amountOut, toToken),
    [amountOut, toToken],
  )
  const displayAmountOut = useMemo(() => formatDisplayAmount(amountOut, 6), [amountOut])
  const minReceivedValue = useMemo(() => {
    if (limitOrderMode) return null
    if (autoRoute === 'amm') {
      return swapStats?.minReceived ?? null
    }
    const output = Number(amountOut || '0')
    if (!output) return null
    return output * (1 - userSlippageTolerance / 10000)
  }, [amountOut, autoRoute, limitOrderMode, swapStats?.minReceived, userSlippageTolerance])
  const routeLabel = useMemo(() => {
    if (autoRoute === 'amm') return 'AMM'
    if (autoRoute === 'orderbook') return 'Orderbook'
    if (autoRoute === 'path') return 'Path'
    return '-'
  }, [autoRoute])
  const showDetails = !limitOrderMode && autoRoute !== 'none' && Number(amountOut) > 0
  const isAmmRoute = autoRoute === 'amm' && Boolean(swapStats)

  useEffect(() => {
    setTxHash('')
    setError('')
  }, [fromKey, toKey, amountIn, limitOrderMode, limitPrice])

  useEffect(() => {
    if (fromKey === toKey && fromKey) {
      setError('Select two different tokens')
    }
  }, [fromKey, toKey])

  useEffect(() => {
    const loadPool = async () => {
      if (!fromToken || !toToken || fromKey === toKey) {
        setPoolInfo(null)
        if (poolRetryRef.current) {
          clearTimeout(poolRetryRef.current)
          poolRetryRef.current = null
        }
        return
      }
      try {
        const info = await fetchBithompAmmByPair(fromToken, toToken)
        setPoolInfo(info)
        if (poolRetryRef.current) {
          clearTimeout(poolRetryRef.current)
          poolRetryRef.current = null
        }
      } catch (poolError) {
        setPoolInfo(null)
        if (!poolRetryRef.current && typeof window !== 'undefined') {
          poolRetryRef.current = window.setTimeout(() => {
            poolRetryRef.current = null
            setPoolRetryId((current) => current + 1)
          }, 5000)
        }
      }
    }
    loadPool()
    return () => {
      if (poolRetryRef.current) {
        clearTimeout(poolRetryRef.current)
        poolRetryRef.current = null
      }
    }
  }, [fromToken, toToken, fromKey, toKey, poolRetryId])

  useEffect(() => {
    const updateBalances = async () => {
      if (!account || !fromToken || !toToken) {
        setBalanceIn('0')
        setBalanceOut('0')
        return
      }
      const [inBalance, outBalance] = await Promise.all([
        fetchBalance(account, fromToken),
        fetchBalance(account, toToken),
      ])
      setBalanceIn(inBalance)
      setBalanceOut(outBalance)
    }
    updateBalances()
  }, [account, fromToken, toToken])

  useEffect(() => {
    const updateTrustline = async () => {
      if (!account || !toToken) {
        setTrustlineMissing(false)
        return
      }
      const hasLine = await hasTrustline(account, toToken)
      setTrustlineMissing(!hasLine)
    }
    updateTrustline()
  }, [account, toToken])

  useEffect(() => {
    if (limitOrderMode || !fromToken || !toToken || fromKey === toKey) {
      setBookQuote(null)
      return
    }

    const inputAmount = Number(amountIn || '0')
    if (!inputAmount) {
      setBookQuote(null)
      return
    }

    const requestId = bookQuoteId.current + 1
    bookQuoteId.current = requestId

    const runQuote = async () => {
      try {
        const quote = await quoteBookSwap({
          account,
          from: fromToken,
          to: toToken,
          amountIn,
        })
        if (bookQuoteId.current !== requestId) {
          return
        }
        if (quote?.amountOut && Number(quote.amountOut) > 0) {
          setBookQuote(quote)
          return
        }
      } catch (quoteError) {
        if (bookQuoteId.current !== requestId) {
          return
        }
      }

      if (bookQuoteId.current === requestId) {
        setBookQuote(null)
      }
    }

    runQuote()
  }, [account, amountIn, fromKey, fromToken, limitOrderMode, toKey, toToken])

  useEffect(() => {
    if (limitOrderMode || !fromToken || !toToken || fromKey === toKey || poolInfo) {
      setPathQuote(null)
      return
    }

    const inputAmount = Number(amountIn || '0')
    if (!inputAmount) {
      setPathQuote(null)
      return
    }
    if (bookQuote?.amountOut && Number(bookQuote.amountOut) > 0) {
      setPathQuote(null)
      return
    }
    if (!account) {
      setPathQuote(null)
      return
    }

    const requestId = pathQuoteId.current + 1
    pathQuoteId.current = requestId

    const runQuote = async () => {
      try {
        const quote = await quotePathSwap({
          account,
          from: fromToken,
          to: toToken,
          amountIn,
        })
        if (pathQuoteId.current !== requestId) {
          return
        }
        if (quote?.amountOut && Number(quote.amountOut) > 0) {
          setPathQuote(quote)
          return
        }
      } catch (quoteError) {
        if (pathQuoteId.current !== requestId) {
          return
        }
      }

      if (pathQuoteId.current === requestId) {
        setPathQuote(null)
      }
    }

    runQuote()
  }, [account, amountIn, bookQuote, fromKey, fromToken, limitOrderMode, poolInfo, toKey, toToken])

  useEffect(() => {
    if (!fromToken || !toToken || fromKey === toKey) {
      setAmountOut('0')
      return
    }

    if (limitOrderMode) {
      const inputAmount = Number(amountIn || '0')
      const limitValue = Number(limitPrice || '0')
      if (!inputAmount || !limitValue) {
        setAmountOut('0')
        return
      }
      const totalOut = inputAmount * limitValue
      setAmountOut(totalOut > 0 ? totalOut.toString() : '0')
      return
    }

    if (autoRoute === 'amm') {
      setAmountOut(ammQuoteOut)
      return
    }

    if (autoRoute === 'orderbook' && bookQuote?.amountOut && Number(bookQuote.amountOut) > 0) {
      setAmountOut(bookQuote.amountOut)
      return
    }

    if (autoRoute === 'path' && pathQuote?.amountOut && Number(pathQuote.amountOut) > 0) {
      setAmountOut(pathQuote.amountOut)
      return
    }

    const inputAmount = Number(amountIn || '0')
    if (!inputAmount) {
      setAmountOut('0')
      return
    }

    const fromPriceXrp = getTokenPriceXrp(fromToken)
    const toPriceXrp = getTokenPriceXrp(toToken)
    if (fromPriceXrp && toPriceXrp) {
      const estimated = inputAmount * (fromPriceXrp / toPriceXrp)
      setAmountOut(estimated > 0 ? estimated.toString() : '0')
      return
    }

    setAmountOut('0')
  }, [
    amountIn,
    autoRoute,
    ammQuoteOut,
    bookQuote,
    fromKey,
    fromToken,
    getTokenPriceXrp,
    limitOrderMode,
    limitPrice,
    pathQuote,
    toKey,
    toToken,
  ])

  useEffect(() => {
    const scheduleTokenInfoRetry = () => {
      if (tokenInfoRetryRef.current || typeof window === 'undefined') {
        return
      }
      tokenInfoRetryRef.current = window.setTimeout(() => {
        tokenInfoRetryRef.current = null
        setTokenInfoRefreshId((current) => current + 1)
      }, 5000)
    }
    const loadTokenInfo = async (token?: XrplCurrency | null) => {
      if (!token || token.isNative || token.currency === 'XRP' || !token.issuer) {
        return
      }
      const key = getTokenKey(token)
      if (getTokenPriceXrp(token) || priceRequests.current.has(key)) {
        return
      }
      priceRequests.current.add(key)
      try {
        const info = await fetchBithompTokenInfo(token)
        if (info) {
          addCustomTokens([info])
        }
        if (!info?.priceXrp && !info?.volume24h) {
          scheduleTokenInfoRetry()
        }
      } catch (priceError) {
        priceRequests.current.delete(key)
        scheduleTokenInfoRetry()
      }
    }

    loadTokenInfo(fromToken)
    loadTokenInfo(toToken)
    return () => {
      if (tokenInfoRetryRef.current) {
        clearTimeout(tokenInfoRetryRef.current)
        tokenInfoRetryRef.current = null
      }
    }
  }, [fromToken, getTokenPriceXrp, toToken, tokenInfoRefreshId])

  const handleSwitch = () => {
    setFromKey(toKey)
    setToKey(fromKey)
  }

  const handleModeChange = (index: number) => {
    const nextLimitMode = index === 1
    setLimitOrderMode(nextLimitMode)
    if (nextLimitMode && (!limitPrice || Number(limitPrice) <= 0)) {
      const baseIn = Number(amountIn || '0')
      const baseOut = Number(amountOut || '0')
      if (baseIn > 0 && baseOut > 0) {
        const nextPrice = baseOut / baseIn
        if (Number.isFinite(nextPrice) && nextPrice > 0) {
          setLimitPrice(nextPrice.toString())
        }
      }
    }
  }

  const handleMax = () => {
    setAmountIn(balanceIn)
  }

  const handleSwap = async () => {
    if (!account || !fromToken || !toToken || fromKey === toKey) return
    setError('')
    try {
      if (limitOrderMode) {
        const expirationMinutes = limitExpiryEnabled ? Number(limitExpiryMinutes) : undefined
        if (limitExpiryEnabled && (!expirationMinutes || expirationMinutes <= 0)) {
          setError('Enter a valid expiration time')
          return
        }
        const result = await createLimitOrder({
          account,
          from: fromToken,
          to: toToken,
          amountIn,
          amountOut,
          expirationMinutes,
          signAndSubmit,
        })
        setTxHash(result.hash)
        setOrdersRefreshId((current) => current + 1)
        return
      }

      const minOutValue = Number(amountOut) * (1 - userSlippageTolerance / 10000)
      if (autoRoute === 'orderbook') {
        const result = await swapOrderBook({
          account,
          from: fromToken,
          to: toToken,
          amountIn,
          expectedOut: amountOut,
          minOut: minOutValue.toString(),
          signAndSubmit,
        })
        setTxHash(result.hash)
        return
      }

      const result = await swapExactIn({
        account,
        from: fromToken,
        to: toToken,
        amountIn,
        expectedOut: amountOut,
        minOut: minOutValue.toString(),
        paths: autoRoute === 'path' ? pathQuote?.paths : undefined,
        signAndSubmit,
      })
      setTxHash(result.hash)
    } catch (swapError) {
      setError(swapError.message || 'Swap failed')
    }
  }

  const handleTrustline = async () => {
    if (!account || !toToken) return
    setError('')
    try {
      await createTrustline({ account, currency: toToken, signAndSubmit })
      setTrustlineMissing(false)
    } catch (trustError) {
      setError(trustError.message || 'Trustline failed')
    }
  }

  const canSubmit = useMemo(() => {
    const hasAmounts = Number(amountIn) > 0 && Number(amountOut) > 0
    if (!limitOrderMode) {
      return hasAmounts
    }
    return hasAmounts && Number(limitPrice) > 0
  }, [amountIn, amountOut, limitOrderMode, limitPrice])

  const swapHelperText = useMemo(() => {
    if (!account || fromKey === toKey || trustlineMissing) {
      return null
    }
    if (!canSubmit) {
      return limitOrderMode ? 'Enter amount and price to place order' : 'Enter an amount to swap'
    }
    return null
  }, [account, canSubmit, fromKey, limitOrderMode, toKey, trustlineMissing])

  const handleCancelOrder = async (sequence: number) => {
    if (!account) return
    setError('')
    try {
      const result = await cancelLimitOrder({ account, offerSequence: sequence, signAndSubmit })
      setTxHash(result.hash)
      setOrdersRefreshId((current) => current + 1)
    } catch (cancelError) {
      setError(cancelError.message || 'Cancel failed')
    }
  }

  return (
    <Page removePadding={false} hideFooterOnDesktop={false} maxWidth="1400px" px={['12px', '16px']}>
      <TradePage>
        <TradeLayout>
          <TradeHeader>
            <PriceHeaderCard>
              <Flex justifyContent="space-between" alignItems="center">
                <PriceHeaderMain>
                  <Text bold fontSize="12px" color="textSubtle">
                    {fromToken && toToken ? `${fromToken.symbol} / ${toToken.symbol}` : 'Select a pair'}
                  </Text>
                  <Text bold fontSize="20px">
                    {headerPriceText}
                  </Text>
                  <Text color={priceChangeColor} bold fontSize="14px">
                    {priceChangeText}
                  </Text>
                </PriceHeaderMain>
                <PriceHeaderStats>
                  <Text fontSize="11px" color="textSubtle">
                    24h Vol
                  </Text>
                  <Text fontSize="12px">{volumeText}</Text>
                  <Text fontSize="11px" color="textSubtle">
                    Liquidity
                  </Text>
                  <Text fontSize="12px">{liquidityText}</Text>
                </PriceHeaderStats>
              </Flex>
            </PriceHeaderCard>
          </TradeHeader>
          <LeftPanel>
            <SwapCard>
              <CurrencyInputHeader
                title={limitOrderMode ? 'Limit' : 'Swap'}
                subtitle={limitOrderMode ? 'Place a limit order on XRPL' : 'Trade tokens in an instant'}
                setIsChartDisplayed={setIsMarketDisplayed}
                isChartDisplayed={isMarketDisplayed}
              />
              <Wrapper id="swap-page">
                {limitOrdersEnabled && (
                  <Flex justifyContent="center" mb="16px">
                    <ButtonMenu
                      activeIndex={limitOrderMode ? 1 : 0}
                      onItemClick={handleModeChange}
                      scale="sm"
                      variant="subtle"
                    >
                      <ButtonMenuItem>Swap</ButtonMenuItem>
                      <ButtonMenuItem>Limit</ButtonMenuItem>
                    </ButtonMenu>
                  </Flex>
                )}
                <AutoColumn gap="8px">
                  <AutoColumn gap="8px">
                    <Text fontSize="12px" color="textSubtle" mb="8px">
                      From
                    </Text>
                    <XrplCurrencyInputPanel
                      id="swap-currency-input"
                      value={amountIn}
                      onUserInput={setAmountIn}
                      onMax={handleMax}
                      showMaxButton
                      label="From"
                      balance={balanceIn}
                      secondaryValue={fromSecondaryValue || undefined}
                      currency={fromToken ?? null}
                      otherCurrency={toToken ?? null}
                      onCurrencySelect={(token) => setFromKey(getTokenKey(token))}
                    />
                  </AutoColumn>

                  {limitOrderMode && (
                    <AutoColumn gap="8px">
                      <Text fontSize="12px" color="textSubtle" mb="8px">
                        {fromToken && toToken
                          ? `Limit Price (${toToken.symbol} per ${fromToken.symbol})`
                          : 'Limit Price'}
                      </Text>
                      <LimitInputContainer>
                        <LimitInputRow>
                          <NumericalInput value={limitPrice} onUserInput={setLimitPrice} placeholder="0.0" />
                        </LimitInputRow>
                      </LimitInputContainer>
                      <Flex alignItems="center" justifyContent="space-between">
                        <Text fontSize="12px" color="textSubtle">
                          Expiration
                        </Text>
                        <Toggle
                          id="toggle-limit-expiration"
                          scale="sm"
                          checked={limitExpiryEnabled}
                          onChange={() => setLimitExpiryEnabled(!limitExpiryEnabled)}
                        />
                      </Flex>
                      {limitExpiryEnabled && (
                        <LimitInputContainer>
                          <LimitInputRow>
                            <NumericalInput
                              value={limitExpiryMinutes}
                              onUserInput={setLimitExpiryMinutes}
                              placeholder="Minutes"
                            />
                            <Text ml="8px" fontSize="12px" color="textSubtle">
                              mins
                            </Text>
                          </LimitInputRow>
                        </LimitInputContainer>
                      )}
                    </AutoColumn>
                  )}

                  <AutoColumn justify="space-between">
                    <AutoRow justify="center" style={{ padding: '0 8px' }}>
                      <ArrowWrapper clickable onClick={handleSwitch}>
                        <ArrowDownIcon width="18px" color="primary" />
                      </ArrowWrapper>
                    </AutoRow>
                  </AutoColumn>

                  <AutoColumn gap="8px">
                    <Text fontSize="12px" color="textSubtle" mb="8px">
                      {limitOrderMode ? 'To' : 'To (estimated)'}
                    </Text>
                    <XrplCurrencyInputPanel
                      id="swap-currency-output"
                      value={displayAmountOut}
                      onUserInput={() => null}
                      showMaxButton={false}
                      label="To"
                      balance={balanceOut}
                      secondaryValue={toSecondaryValue || undefined}
                      currency={toToken ?? null}
                      otherCurrency={fromToken ?? null}
                      onCurrencySelect={(token) => setToKey(getTokenKey(token))}
                    />
                  </AutoColumn>
                  <AutoColumn gap="8px" style={{ padding: '0 16px' }}>
                    <RowBetween align="center">
                      <Text fontSize="12px" color="textSubtle">
                        {limitOrderMode ? 'Limit price' : 'Price'}
                      </Text>
                      <TradePrice
                        price={priceForDisplay}
                        showInverted={showInverted}
                        setShowInverted={setShowInverted}
                      />
                    </RowBetween>
                  </AutoColumn>
                </AutoColumn>
                <Box mt="8px">
                  {swapHelperText && (
                    <Text color="textSubtle" fontSize="12px" mb="8px">
                      {swapHelperText}
                    </Text>
                  )}
                  {tokensError && (
                    <Text color="failure" mb="8px">
                      {tokensError}
                    </Text>
                  )}
                  {!account ? (
                    <SwapConnectButton width="100%" />
                  ) : fromKey === toKey ? (
                    <SwapCtaButton width="100%" disabled>
                      Select different tokens
                    </SwapCtaButton>
                  ) : trustlineMissing ? (
                    <SwapCtaButton width="100%" onClick={handleTrustline}>
                      Create Trustline
                    </SwapCtaButton>
                  ) : (
                    <SwapCtaButton width="100%" onClick={handleSwap} disabled={!canSubmit}>
                      {limitOrderMode ? 'Place Limit Order' : 'Swap'}
                    </SwapCtaButton>
                  )}
                  {txHash && (
                    <TxRow>
                      <Text fontSize="12px" color="textSubtle">
                        Transaction
                      </Text>
                      <TxLink href={getXrplTxUrl(txHash)} target="_blank" rel="noreferrer">
                        {formatTxHash(txHash)}
                        <TxLinkHint>View on XRPSCAN</TxLinkHint>
                      </TxLink>
                    </TxRow>
                  )}
                  {error && (
                    <Text color="failure" mt="16px">
                      {error}
                    </Text>
                  )}
                </Box>
              </Wrapper>
            </SwapCard>
            {showDetails && (
              <DetailsCard>
                <AutoColumn>
                  <RowBetween>
                    <RowFixed>
                      <Text fontSize="12px" color="textSubtle">
                        Minimum received
                      </Text>
                      <QuestionHelper
                        text="Your transaction will revert if there is a large, unfavorable price movement before it is confirmed."
                        ml="8px"
                      />
                    </RowFixed>
                    <Text fontSize="14px">
                      {minReceivedValue !== null ? formatAmount(minReceivedValue, 6) : '-'} {toToken?.symbol}
                    </Text>
                  </RowBetween>
                  <RowBetween>
                    <RowFixed>
                      <Text fontSize="12px" color="textSubtle">
                        Price Impact
                      </Text>
                      <QuestionHelper
                        text={
                          isAmmRoute
                            ? 'The difference between the market price and estimated price due to trade size.'
                            : 'Price impact applies to AMM swaps. Order book and pathfinding route to available offers.'
                        }
                        ml="8px"
                      />
                    </RowFixed>
                    <ErrorText
                      fontSize="14px"
                      severity={getPriceImpactSeverity(isAmmRoute ? swapStats?.priceImpactPct : undefined)}
                    >
                      {isAmmRoute && swapStats?.priceImpactPct !== undefined
                        ? swapStats.priceImpactPct < 0.01
                          ? '<0.01%'
                          : `${swapStats.priceImpactPct.toFixed(2)}%`
                        : '-'}
                    </ErrorText>
                  </RowBetween>
                  <RowBetween>
                    <RowFixed>
                      <Text fontSize="12px" color="textSubtle">
                        Liquidity Provider Fee
                      </Text>
                      <QuestionHelper
                        text={
                          isAmmRoute ? (
                            <>
                              <Text mb="16px">For each trade a {(swapStats?.feeRate || 0) * 100}% fee is paid</Text>
                              <Text>
                                - {formatAmount(swapStats?.lpFee || 0)} {fromToken?.symbol} to LPs
                              </Text>
                            </>
                          ) : (
                            'Liquidity provider fees apply only to AMM swaps.'
                          )
                        }
                        ml="8px"
                      />
                    </RowFixed>
                    <Text fontSize="14px">
                      {isAmmRoute && swapStats?.lpFee ? formatAmount(swapStats.lpFee) : '-'} {fromToken?.symbol}
                    </Text>
                  </RowBetween>
                  <RowBetween>
                    <RowFixed>
                      <Text fontSize="12px" color="textSubtle">
                        Route
                      </Text>
                      <QuestionHelper
                        text="Auto routes between AMM and order book to seek the best execution. Pathfinding is used as a fallback."
                        ml="8px"
                      />
                    </RowFixed>
                    <Text fontSize="14px">{autoRoute !== 'none' ? `Auto (${routeLabel})` : '-'}</Text>
                  </RowBetween>
                  <RowBetween>
                    <Text fontSize="12px" color="textSubtle">
                      Slippage Tolerance
                    </Text>
                    <Text fontSize="14px">{slippagePct.toFixed(2)}%</Text>
                  </RowBetween>
                </AutoColumn>
              </DetailsCard>
            )}
          </LeftPanel>
          <RightPanel>
            {isMarketDisplayed && (
              <MarketCard $active>
                <AutoColumn gap="8px">
                  <MarketHeader>
                    <MarketMeta>
                      <Text fontSize="11px" color="textSubtle">
                        Market
                      </Text>
                      <Text bold fontSize="12px">
                        {fromToken && toToken ? `${fromToken.symbol}/${toToken.symbol}` : 'Select a pair'}
                      </Text>
                      {marketPrice ? (
                        <MarketPriceLine>
                          <MarketPrice>
                            {marketPrice.value} {marketPrice.symbol}
                          </MarketPrice>
                          <MarketPriceSub>
                            {marketPrice.usd ? `(~$${formatAmount(marketPrice.usd, 2)})` : ''}
                          </MarketPriceSub>
                        </MarketPriceLine>
                      ) : (
                        <MarketPriceLine>
                          <MarketPrice>-</MarketPrice>
                        </MarketPriceLine>
                      )}
                    </MarketMeta>
                    <Flex alignItems="center" style={{ gap: '16px' }}>
                      {marketView === 'orderbook' && (
                        <Button
                          scale="sm"
                          variant="tertiary"
                          onClick={() => setOrderBookRefreshId((current) => current + 1)}
                        >
                          Refresh
                        </Button>
                      )}
                      <MarketTabs>
                        <MarketTab
                          type="button"
                          $active={marketView === 'orderbook'}
                          onClick={() => setMarketView('orderbook')}
                        >
                          Order Book
                        </MarketTab>
                        <MarketTab
                          type="button"
                          $active={marketView === 'chart'}
                          onClick={() => setMarketView('chart')}
                        >
                          Chart
                        </MarketTab>
                      </MarketTabs>
                    </Flex>
                  </MarketHeader>
                  {marketView === 'orderbook' ? (
                    <MarketRow>
                      {orderBookLoading && <Text color="textSubtle">Loading order book...</Text>}
                      {!orderBookLoading && orderBookError && <Text color="failure">{orderBookError}</Text>}
                      {!orderBookLoading && !orderBookError && !orderBook && (
                        <Text color="textSubtle">Select a pair to view the order book.</Text>
                      )}
                      {!orderBookLoading && !orderBookError && orderBook && (
                        <OrderbookTable>
                          <OrderbookMidRow>
                            <Text fontSize="11px" color="textSubtle">
                              Mid Price
                            </Text>
                            <Text bold fontSize="12px">
                              {orderBookMidDisplay
                                ? `${orderBookMidDisplay.value} ${orderBookMidDisplay.symbol}`
                                : '-'}
                            </Text>
                            <Text fontSize="11px" color="textSubtle">
                              {orderBookSummary
                                ? `Spread: ${formatAmount(orderBookSummary.spread, 6)}`
                                : 'Spread: —'}
                            </Text>
                          </OrderbookMidRow>
                          <OrderbookGrid>
                            <OrderbookSide $side="ask">
                              <Text bold fontSize="14px" mb="8px">
                                Asks
                              </Text>
                              <OrderbookHeader>
                                <OrderbookCell $side="ask" $flow="in" color="textSubtle">
                                  Price
                                </OrderbookCell>
                                <OrderbookCell $side="ask" $flow="in" color="textSubtle">
                                  Amount
                                </OrderbookCell>
                                <OrderbookCell $side="ask" $flow="in" color="textSubtle">
                                  Total
                                </OrderbookCell>
                              </OrderbookHeader>
                              {orderBookDepth.asks.map((row) => (
                                <OrderbookRow
                                  key={`ask-${row.price}-${row.amount}-${row.total}`}
                                  $side="ask"
                                  $depth={orderBookDepth.maxAsk ? row.depth / orderBookDepth.maxAsk : 0}
                                >
                                  <OrderbookCell $side="ask" $flow="in">
                                    {formatAmount(row.price, 6)}
                                  </OrderbookCell>
                                  <OrderbookCell $side="ask" $flow="in">
                                    {formatAmount(row.amount, 6)}
                                  </OrderbookCell>
                                  <OrderbookCell $side="ask" $flow="in" $muted>
                                    {formatAmount(row.total, 6)}
                                  </OrderbookCell>
                                </OrderbookRow>
                              ))}
                              {!orderBook.asks?.length && (
                                <OrderbookEmptyText mt="8px">
                                  No sell orders yet — place the first ask and set the market
                                </OrderbookEmptyText>
                              )}
                            </OrderbookSide>
                            <OrderbookDivider />
                            <OrderbookSide $side="bid">
                              <Text bold fontSize="14px" mb="8px">
                                Bids
                              </Text>
                              <OrderbookHeader>
                                <OrderbookCell $side="bid" $flow="in" color="textSubtle">
                                  Price
                                </OrderbookCell>
                                <OrderbookCell $side="bid" $flow="in" color="textSubtle">
                                  Amount
                                </OrderbookCell>
                                <OrderbookCell $side="bid" $flow="in" color="textSubtle">
                                  Total
                                </OrderbookCell>
                              </OrderbookHeader>
                              {orderBookDepth.bids.map((row) => (
                                <OrderbookRow
                                  key={`bid-${row.price}-${row.amount}-${row.total}`}
                                  $side="bid"
                                  $depth={orderBookDepth.maxBid ? row.depth / orderBookDepth.maxBid : 0}
                                >
                                  <OrderbookCell $side="bid" $flow="in">
                                    {formatAmount(row.price, 6)}
                                  </OrderbookCell>
                                  <OrderbookCell $side="bid" $flow="in">
                                    {formatAmount(row.amount, 6)}
                                  </OrderbookCell>
                                  <OrderbookCell $side="bid" $flow="in" $muted>
                                    {formatAmount(row.total, 6)}
                                  </OrderbookCell>
                                </OrderbookRow>
                              ))}
                              {!orderBook.bids?.length && (
                                <OrderbookEmptyText mt="8px">No bids available</OrderbookEmptyText>
                              )}
                            </OrderbookSide>
                          </OrderbookGrid>
                        </OrderbookTable>
                      )}
                    </MarketRow>
                  ) : (
                    <MarketRow>
                      {chartSymbol ? (
                        <ChartContainer>
                          <TradingView id="XRPL_SWAP_CHART" symbol={chartSymbol} />
                        </ChartContainer>
                      ) : (
                        <Text color="textSubtle">Chart unavailable for this pair yet.</Text>
                      )}
                    </MarketRow>
                  )}
                </AutoColumn>
              </MarketCard>
            )}
            {limitOrdersEnabled && account && (
              <OrdersCard>
                <AutoColumn gap="16px">
                  <RowBetween align="center">
                    <Text bold>Open Orders</Text>
                    <Text fontSize="12px" color="textSubtle">
                      {formattedOrders.length}
                    </Text>
                  </RowBetween>
                  {openOrdersLoading && <Text color="textSubtle">Loading open orders...</Text>}
                  {!openOrdersLoading && openOrdersError && <Text color="failure">{openOrdersError}</Text>}
                  {!openOrdersLoading && !openOrdersError && !formattedOrders.length && (
                    <Text color="textSubtle">No open orders</Text>
                  )}
                  {!openOrdersLoading &&
                    !openOrdersError &&
                    formattedOrders.map(({ order, sellAmount, buyAmount, price, expiration }) => (
                      <OrderRow key={order.sequence}>
                        <Flex
                          flexDirection={isMobile ? 'column' : 'row'}
                          alignItems={isMobile ? 'flex-start' : 'center'}
                          justifyContent="space-between"
                        >
                          <Flex flexDirection="column">
                            <Text bold>
                              {formatAmount(sellAmount.value)} {sellAmount.symbol}
                            </Text>
                            <Text fontSize="12px" color="textSubtle">
                              for {formatAmount(buyAmount.value)} {buyAmount.symbol}
                            </Text>
                            {!sellAmount.isNative && sellAmount.issuer && (
                              <Text fontSize="12px" color="textSubtle">
                                Sell issuer: {shortIssuer(sellAmount.issuer)}
                              </Text>
                            )}
                            {!buyAmount.isNative && buyAmount.issuer && (
                              <Text fontSize="12px" color="textSubtle">
                                Buy issuer: {shortIssuer(buyAmount.issuer)}
                              </Text>
                            )}
                          </Flex>
                          <Flex
                            flexDirection="column"
                            alignItems={isMobile ? 'flex-start' : 'flex-end'}
                            mt={isMobile ? '8px' : '0'}
                          >
                            <Text fontSize="12px" color="textSubtle">
                              Price
                            </Text>
                            <Text>
                              {price ? `${formatAmount(price)} ${buyAmount.symbol}/${sellAmount.symbol}` : '-'}
                            </Text>
                            <Text fontSize="12px" color="textSubtle">
                              Expires
                            </Text>
                            <Text>{expiration}</Text>
                          </Flex>
                          <Button
                            scale="sm"
                            variant="secondary"
                            mt={isMobile ? '8px' : '0'}
                            onClick={() => handleCancelOrder(order.sequence)}
                          >
                            Cancel
                          </Button>
                        </Flex>
                      </OrderRow>
                    ))}
                </AutoColumn>
              </OrdersCard>
            )}
          </RightPanel>
        </TradeLayout>
      </TradePage>
    </Page>
  )
}

export default Swap
